<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { onScopeDispose } from 'vue'
import { watchDebounced } from '@vueuse/core'
import ConciergeMadlibsBlank from './ConciergeMadlibsBlank.vue'
import { canonicalSignalValue, conformSignalText, recognizesSignalText } from './conformSignalText'
import { signalFilled, useConciergeState } from './conciergeState'
import { SUGGESTION_POOLS } from './signalDictionary'
import { useTypewriterPlaceholder } from './useTypewriterPlaceholder'
import { recommendationReadiness } from './recommendationRequest'
import { trackConciergeEvent } from './conciergeEventTracking'

// Second concierge input UI: the buyer fills blanks in a sentence instead of
// writing one. It shares the whole contract with ConciergeInput — same store,
// same request builder, same readiness rule, same live-as-you-type fetch — and
// differs only in how signals get populated. Blanks write their signal directly,
// so this variant needs no parser. Like the free-text input it only writes signals
// — the results surface watches those and fetches.
//
// `framed` wraps it in the landing page's own section shape — a white panel the
// rainbow only tints — for placement between other sections. Off by default so the
// component stays a bare sentence wherever something else owns the surface.
withDefaults(defineProps<{
  framed?: boolean
}>(), { framed: false, })

const state = useConciergeState()

type BlankSignal = 'relationship' | 'location' | 'interests' | 'occasion'

// The interests blank holds a list. People write it as prose after "really into" —
// "pizza and coffee", "hiking, jazz & baking" — so an entry ends at a comma,
// ampersand, or a standalone "and", not commas alone.
const INTEREST_SEPARATOR = /\s*(?:,|&|\band\b)\s*/i

// The blanks own what's typed so spaces and commas survive a keystroke; the
// store owns the contract. Writes go straight through, and syncTextFromState
// pulls values back when something else fills the store (persisted state from
// an earlier page).
const text = _ref<Record<BlankSignal, string>>({
  relationship: '',
  location: '',
  interests: '',
  occasion: '',
})

function syncTextFromState () {
  const { relationship, location, interests, occasion } = state.signals
  text.value.relationship = relationship.display ?? relationship.value ?? ''
  text.value.location = location.display ?? location.value ?? ''
  text.value.interests = interests.display ?? interests.value.join(', ')
  text.value.occasion = occasion.display ?? occasion.value ?? ''
}

syncTextFromState()

// A blank writes both halves of its signal: the canonical the endpoint matches on,
// and the buyer's own wording for everything that renders. "our anniversary" stays
// on screen and reaches the request as "anniversary", and it survives a reload
// that way too, since the store persists both. Reads the live text, so the write
// scheduler and the blur flush share one path.
// Idempotent: a write only lands when the resolved signal actually differs from
// what's stored. Blurring a blank (tabbing through a filled sentence) re-commits
// the same text, and setSignal swaps in a fresh object every time — which
// invalidates the results surface's query and refetches for no reason. Comparing
// first keeps an unchanged field from touching the store at all.
function commitBlank (name: BlankSignal) {
  cancelWrite(name)

  const typed = text.value[name].trim()
  if (!typed) {
    if (signalFilled(state.signals[name])) state.clearSignal(name)
    return
  }

  if (name === 'interests') {
    const current = state.signals.interests
    const entries = typed.split(INTEREST_SEPARATOR).map(entry => entry.trim()).filter(Boolean)
    // Interests are open vocabulary, so an unmatched entry falls back to itself.
    const values = [...new Set(entries.map(entry => canonicalSignalValue('interests', entry) ?? entry))]
    if (current.display === typed && sameValues(current.value, values)) return
    state.setSignal('interests', values, { display: typed })
    return
  }

  // A null value is a real commit: the buyer's words render from `display`, but an
  // occasion/relationship no list resolves sends nothing — an unmatched value in
  // those exclusive filters would exclude every gift tagged with a real one.
  const value = canonicalSignalValue(name, typed)
  const current = state.signals[name]
  if (!Array.isArray(current.value) && current.value === value && current.display === typed) return
  state.setSignal(name, value, { display: typed })
}

function sameValues (a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index])
}

// Signals used to reach the store on every keystroke, which had the results surface
// searching half-typed words ("s", "se", "sea"...). Now a write is timed to what was
// typed: a word we recognise lands at once, a prefix of one is held until it's
// finished, and anything else waits a beat in case more is still coming — so a
// partial word never seeds a search, while a settled long-tail value ("pottery")
// still does. Clearing or leaving a blank always writes straight through.
const SIGNAL_WRITE_DELAY_MS = 2000

type WriteTiming = 'now' | 'wait' | 'hold'

const writeTimers = new Map<BlankSignal, ReturnType<typeof setTimeout>>()

function cancelWrite (name: BlankSignal) {
  const timer = writeTimers.get(name)
  if (timer === undefined) return
  clearTimeout(timer)
  writeTimers.delete(name)
}

// The entry being typed right now. For interests that's the part after the last
// separator — the finished entries before it have already settled.
function pendingEntry (name: BlankSignal): string {
  if (name !== 'interests') return text.value[name]
  return lastInterestEntry(text.value.interests)
}

// The tail after the final separator, e.g. "pizza and cof" → "cof". A trailing
// separator ("pizza and ") leaves an empty tail, which settles the finished ones.
function lastInterestEntry (value: string): string {
  const matches = [...value.matchAll(new RegExp(INTEREST_SEPARATOR, 'gi'))]
  const last = matches.at(-1)
  return last ? value.slice(last.index + last[0].length) : value
}

function writeTiming (name: BlankSignal): WriteTiming {
  const entry = pendingEntry(name).trim()
  // Empty is a cleared blank or a just-finished interest entry — settle it now.
  if (!entry) return 'now'
  // Too little to judge, or a prefix a completion is still offered for: they're
  // mid-word, so it isn't searched at all.
  if (entry.length < MIN_SUGGESTION_CHARS) return 'hold'
  if (shortestCompletion(SUGGESTION_POOLS[name], entry)) return 'hold'
  // A term the matcher already knows can search at once; anything else is a
  // complete-but-unknown value, so give the buyer a beat to keep typing.
  return recognizesSignalText(name, entry) ? 'now' : 'wait'
}

function scheduleWrite (name: BlankSignal) {
  cancelWrite(name)
  const timing = writeTiming(name)
  if (timing === 'hold') return
  if (timing === 'now') {
    commitBlank(name)
    return
  }
  writeTimers.set(name, setTimeout(() => commitBlank(name), SIGNAL_WRITE_DELAY_MS))
}

onScopeDispose(() => {
  for (const timer of writeTimers.values()) clearTimeout(timer)
  writeTimers.clear()
})

// Typing updates the blank immediately — the input, its ghost completion and the
// placeholder all read this — then schedules the store write on the timing above.
let interacted = false
function setBlank (name: BlankSignal, value: string) {
  if (!interacted) {
    interacted = true
    trackConciergeEvent('first_interaction', { variant: 'madlibs' })
  }
  text.value[name] = value
  scheduleWrite(name)
}

// TEMPORARY (concept-proving): hardcoded example pools. Real versions come from
// popular-city and popular-interest data, not these lists.
const PLACEHOLDER_EXAMPLES: Record<BlankSignal, string[]> = {
  relationship: ['roommate', 'sister', 'coworker', 'best friend', 'dad'],
  location: ['Seattle', 'Austin', 'Chicago', 'Nashville', 'Denver'],
  interests: ['pottery', 'coffee', 'hiking', 'jazz', 'gardening'],
  occasion: ['no reason at all', 'their birthday', 'our anniversary', 'a new job'],
}

// An empty blank holds the width of the longest example it rotates through, so
// the typewriter animates inside a still box — otherwise every few letters the
// sentence re-wraps and the end of the first line bounces between lines.
function longestExample (examples: string[]): string {
  return examples.reduce((widest, example) => (example.length > widest.length ? example : widest))
}

const PLACEHOLDER_RESERVE = Object.fromEntries(
  Object.entries(PLACEHOLDER_EXAMPLES).map(([name, examples]) => [name, longestExample(examples)]),
) as Record<BlankSignal, string>

// Only one blank retypes its example at a time, in reading order, so the eye is
// drawn through the sentence instead of four fields flickering at once. A blank
// the buyer has filled or is currently typing in loses its turn, so once they're
// all filled the rotation goes quiet on its own.
//
// The handoff rides each typewriter's own cycle boundary rather than a timer: a
// blank erases a letter at a time, retypes, and only then passes the turn along.
// A timer would cut mid-word, leaving the pill showing a fragment like "coffe" —
// and erasing letter by letter is what lets these self-sizing pills shrink
// smoothly instead of snapping to the next width.
const TURN_ORDER: BlankSignal[] = ['relationship', 'location', 'interests', 'occasion']

let turnIndex = _ref(0)
let focusedBlank = _ref<BlankSignal | null>(null)

// The takeover teleport blurs then re-focuses the current blank on engage; emit only when
// the blank changes so that restore doesn't log a second input_focus.
let lastFocusTracked: BlankSignal | null = null

// Spelling gets fixed on the way out, never mid-keystroke: rewriting the field
// while someone is still typing in it fights them for the caret.
function setFocusedBlank (name: BlankSignal, focused: boolean) {
  focusedBlank.value = focused ? name : null
  if (focused) {
    if (name !== lastFocusTracked) {
      lastFocusTracked = name
      trackConciergeEvent('input_focus', { variant: 'madlibs', signalKind: name })
    }
    return
  }

  const corrected = conformSignalText(name, text.value[name])
  if (corrected && corrected !== text.value[name]) text.value[name] = corrected

  // Leaving a blank commits whatever's in it ahead of any pending wait — the buyer
  // has moved on, so there's nothing left to wait for.
  commitBlank(name)
}

function availableForTurn (name: BlankSignal): boolean {
  return !text.value[name]
}

// The whole sentence goes still the moment the buyer is in any blank — motion
// elsewhere on the line is a distraction while they're typing, and it's what was
// re-wrapping the sentence under them.
function isTurn (name: BlankSignal): boolean {
  return focusedBlank.value === null && TURN_ORDER[turnIndex.value] === name && availableForTurn(name)
}

// Taking a suggestion should carry you onward, not park you at the end of a word
// you just accepted — the sentence is meant to be filled left to right.
const blankRefs: Partial<Record<BlankSignal, { focus: () => void } | null>> = {}

function registerBlank (name: BlankSignal, instance: unknown) {
  blankRefs[name] = instance as { focus: () => void } | null
}

function focusNextBlank (name: BlankSignal) {
  const next = TURN_ORDER[TURN_ORDER.indexOf(name) + 1]
  if (next) blankRefs[next]?.focus()
}

function passTurn () {
  for (let step = 1; step <= TURN_ORDER.length; step++) {
    const candidate = (turnIndex.value + step) % TURN_ORDER.length
    if (availableForTurn(TURN_ORDER[candidate])) {
      turnIndex.value = candidate
      return
    }
  }
}

// Without this the rotation would stall: the blank holding the turn stops
// animating the moment it's filled or focused, and a stopped typewriter never
// reaches the cycle boundary that would pass the turn on.
watch(
  () => TURN_ORDER.map(availableForTurn),
  () => {
    if (!availableForTurn(TURN_ORDER[turnIndex.value])) passTurn()
  },
  { immediate: true },
)

// Paced like a person, not a terminal. The composable's defaults are tuned for
// the free-text input's long sentences, where a whole clause has to appear before
// attention moves on; here it's one short word, so the same speed reads as a
// blink. Erasing runs faster than typing, the way holding backspace does.
const TYPEWRITER = {
  typeMs: 115,
  deleteMs: 55,
  holdMs: 2200,
  onCycle: passTurn,
}

const relationshipPlaceholder = (useTypewriterPlaceholder(
  PLACEHOLDER_EXAMPLES.relationship, () => isTurn('relationship'), TYPEWRITER))
const locationPlaceholder = (useTypewriterPlaceholder(
  PLACEHOLDER_EXAMPLES.location, () => isTurn('location'), TYPEWRITER))
const interestsPlaceholder = (useTypewriterPlaceholder(
  PLACEHOLDER_EXAMPLES.interests, () => isTurn('interests'), TYPEWRITER))
const occasionPlaceholder = (useTypewriterPlaceholder(
  PLACEHOLDER_EXAMPLES.occasion, () => isTurn('occasion'), TYPEWRITER))

// Inline completions come from the same curated word lists the parser matches
// against, so a blank suggests only values the backend will recognise. Two
// characters before offering anything — one letter matches too much to be useful.
const MIN_SUGGESTION_CHARS = 2

// Shortest match wins: it's the least presumptuous completion of what's been
// typed, and ties resolve alphabetically so the suggestion never flickers between
// equally-good candidates.
function shortestCompletion (pool: string[], fragment: string): string {
  const lower = fragment.toLowerCase()
  let best = ''

  for (const candidate of pool) {
    const candidateLower = candidate.toLowerCase()
    // Already a whole word from the list — stop offering a longer one, or
    // accepting "coworker" would immediately dangle an "s" for "coworkers".
    if (candidateLower === lower) return ''
    if (candidate.length <= fragment.length) continue
    if (!candidateLower.startsWith(lower)) continue
    if (!best || candidate.length < best.length || (candidate.length === best.length && candidate < best))
      best = candidate
  }

  return best
}

function suggestionFor (name: BlankSignal): string {
  const typed = text.value[name]

  // The interests blank holds a list, so only the entry being typed is a
  // completion candidate — the finished ones stay untouched.
  if (name === 'interests') {
    const fragment = lastInterestEntry(typed).trimStart()
    if (fragment.length < MIN_SUGGESTION_CHARS) return ''

    const match = shortestCompletion(SUGGESTION_POOLS.interests, fragment)
    return match ? typed.slice(0, typed.length - fragment.length) + match : ''
  }

  if (typed.length < MIN_SUGGESTION_CHARS) return ''
  return shortestCompletion(SUGGESTION_POOLS[name], typed)
}

const relationshipSuggestion = _computed(() => suggestionFor('relationship'))
const locationSuggestion = _computed(() => suggestionFor('location'))
const interestsSuggestion = _computed(() => suggestionFor('interests'))
const occasionSuggestion = _computed(() => suggestionFor('occasion'))

// Assembled from the filled blanks so the request carries a coherent `query`
// sentence whichever UI produced it — the endpoint can't tell the variants apart.
function synthesizeQuery (): string {
  const { relationship, location, interests, occasion } = state.signals
  const clauses = [`Find something for my ${relationship.display ?? relationship.value ?? 'someone'}`]
  if (location.value) clauses.push(`in ${location.display ?? location.value}`)
  if (interests.value.length)
    clauses.push(`who's really into ${interests.display ?? interests.value.join(' and ')}`)

  const sentence = clauses.join(' ')
  const reason = occasion.display ?? occasion.value
  return reason ? `${sentence} — it's for ${reason}.` : `${sentence}.`
}

const readiness = _computed(() => recommendationReadiness(state.signals))

// The results surface fetches off the store's signals, but gates that fetch on
// state.raw being non-empty — so, like the free-text input, this variant has to
// populate raw with the synthesized sentence once there's enough to search, and
// clear it back to '' when readiness drops. Debounced so it settles when the buyer
// stops rather than rewriting on every keystroke.
watchDebounced(
  () => state.signals,
  () => {
    state.raw = readiness.value.ready ? synthesizeQuery() : ''
  },
  { debounce: 400, deep: true, immediate: true },
)
</script>

<template>
  <div class="concierge-madlibs">
    <div class="panel" :class="{ 'panel--framed': framed }">
      <p class="sentence">
        Find something for my
        <ConciergeMadlibsBlank
          :ref="instance => registerBlank('relationship', instance)"
          label="Recipient"
          tone="recipient"
          :reserve="PLACEHOLDER_RESERVE.relationship"
          :placeholder="relationshipPlaceholder"
          :modelValue="text.relationship"
          :suggestion="relationshipSuggestion"
          @update:modelValue="value => setBlank('relationship', value)"
          @focusChange="focused => setFocusedBlank('relationship', focused)"
          @accepted="focusNextBlank('relationship')"
        />
        in
        <ConciergeMadlibsBlank
          :ref="instance => registerBlank('location', instance)"
          label="City"
          tone="city"
          :reserve="PLACEHOLDER_RESERVE.location"
          :placeholder="locationPlaceholder"
          :modelValue="text.location"
          :suggestion="locationSuggestion"
          @update:modelValue="value => setBlank('location', value)"
          @focusChange="focused => setFocusedBlank('location', focused)"
          @accepted="focusNextBlank('location')"
        />
        who's really into
        <ConciergeMadlibsBlank
          :ref="instance => registerBlank('interests', instance)"
          label="Interest"
          tone="interest"
          :reserve="PLACEHOLDER_RESERVE.interests"
          :placeholder="interestsPlaceholder"
          :modelValue="text.interests"
          :suggestion="interestsSuggestion"
          @update:modelValue="value => setBlank('interests', value)"
          @focusChange="focused => setFocusedBlank('interests', focused)"
          @accepted="focusNextBlank('interests')"
        />
        — it's for
        <ConciergeMadlibsBlank
          :ref="instance => registerBlank('occasion', instance)"
          label="Occasion"
          tone="occasion"
          :reserve="PLACEHOLDER_RESERVE.occasion"
          :placeholder="occasionPlaceholder"
          :modelValue="text.occasion"
          :suggestion="occasionSuggestion"
          @update:modelValue="value => setBlank('occasion', value)"
          @focusChange="focused => setFocusedBlank('occasion', focused)"
          @accepted="focusNextBlank('occasion')"
        />.
      </p>
    </div>

  </div>
</template>

<style lang="scss" scoped>
@use "./conciergeRainbow" as rainbow;

// The top padding is structural, not decoration: a focused blank floats its
// label above the pill, and on the first line that label would otherwise be
// clipped by the container's edge.
// Matches the section banners elsewhere in the app (AddGiftPreferencesBanner):
// white, a soft shadow, rounded-v2lg's 20px, and a coloured accent along the
// bottom. Theirs is a flat 4px; this one is a slowly panning rainbow at 3px,
// since moving colour carries more weight than a solid rule. Painted as a real
// border rather than a strip, using the two-background trick
// from .calendar-gradient-box: white clipped to the padding box, the gradient
// filling the border box, and a transparent border letting it through. A strip
// stopped square at each end; a border follows the radius, so the colour curves
// up around the bottom corners.
.panel--framed {
  animation: concierge-rainbow-pan 8s linear infinite;
  background:
    linear-gradient(var(--color-bg-surface-white), var(--color-bg-surface-white)) padding-box,
    rainbow.$gradient border-box;
  background-size: auto, 200% 100%;
  border: 0 solid transparent;
  border-bottom-width: 3px;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgb(0 0 0 / 8%);
  padding: 24px 20px;
}

@media (prefers-reduced-motion: reduce) {
  .panel--framed {
    animation: none;
  }
}

.concierge-madlibs {
  margin: 0 auto;
  max-width: 620px;
  padding-top: 24px;
}

// Roomy line-height: the blanks are boxes, not bare text, so consecutive lines
// would otherwise sit edge to edge — and a focused blank floats its label above
// itself, which needs somewhere to sit when the sentence wraps.
.sentence {
  font-size: 21px;

  // Sets the gap between wrapped rows, since the blanks hold their own height.
  // Stated in px because it's arithmetic, not rhythm: 48px blank + 12px gap.
  line-height: 60px;
  margin: 0;
  text-align: center;
}

</style>
